import { useEffect, useState } from "react";
import { PageHeader } from "@/components/Layout";
import MapView from "@/components/MapView";
import DispatchMessages from "@/components/DispatchMessages";
import DispatchPTT from "@/components/DispatchPTT";
import api from "@/lib/api";
import { LayoutDashboard, Bus, Users, MapPin, Clock, Bell, ShieldAlert, Route as RouteIcon, CheckCircle2, PlayCircle } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

const STAT = [
  { key: "routes_today", label: "Routes aujourd'hui", icon: RouteIcon },
  { key: "routes_in_progress", label: "En cours", icon: PlayCircle },
  { key: "routes_completed", label: "Terminées", icon: CheckCircle2 },
  { key: "routes_not_started", label: "Non démarrées", icon: Clock },
  { key: "active_drivers", label: "Chauffeurs actifs", icon: Users },
  { key: "active_buses", label: "Autobus actifs", icon: Bus },
  { key: "total_stops", label: "Arrêts", icon: MapPin },
  { key: "delays", label: "Retards", icon: Clock },
  { key: "alerts", label: "Alertes", icon: Bell },
  { key: "needs_validation", label: "À valider", icon: ShieldAlert },
];

const fmtAge = (s) => (s == null ? "—" : s < 60 ? `${s} s` : s < 3600 ? `${Math.round(s / 60)} min` : `${Math.round(s / 3600)} h`);

export default function Dashboard() {
  const [stats, setStats] = useState({});
  const [live, setLive] = useState({ vehicles: [] });
  const [alerts, setAlerts] = useState([]);

  const load = () => {
    api.get("/dispatch/dashboard").then((r) => setStats(r.data)).catch(() => {});
    api.get("/dispatch/live").then((r) => setLive(r.data)).catch(() => {});
    api.get("/dispatch/alerts").then((r) => setAlerts(r.data)).catch(() => {});
  };
  useEffect(() => { load(); const t = setInterval(load, 5000); return () => clearInterval(t); }, []);

  const resolve = async (id) => { await api.post(`/dispatch/alerts/${id}/resolve`); load(); };
  const sevColor = { CRITICAL: "bg-red-500", WARNING: "bg-amber-500", INFO: "bg-blue-500" };

  return (
    <div>
      <PageHeader title="Tableau de bord" subtitle="Supervision des routes et véhicules en temps réel" icon={LayoutDashboard} />
      <div className="p-8 space-y-6">
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-px bg-border border border-border rounded-lg overflow-hidden">
          {STAT.map((s) => {
            const Icon = s.icon;
            return (
              <div key={s.key} className="bg-card p-5" data-testid={`stat-${s.key}`}>
                <div className="flex items-center justify-between">
                  <span className="overline">{s.label}</span>
                  <Icon className="w-4 h-4 text-slate-300" />
                </div>
                <div className="font-display text-3xl font-bold mt-2">{stats[s.key] ?? "—"}</div>
              </div>
            );
          })}
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
          <div className="xl:col-span-2 rounded-lg border border-border overflow-hidden bg-card">
            <div className="px-5 py-3 border-b border-border flex items-center justify-between">
              <h3 className="font-display font-semibold">Position des véhicules actifs</h3>
              <Badge variant="secondary">{live.vehicles.length} en route</Badge>
            </div>
            <div className="h-[460px]">
              <MapView vehicles={live.vehicles} stops={[]} />
            </div>
          </div>
          <div className="rounded-lg border border-border bg-card flex flex-col">
            <div className="px-5 py-3 border-b border-border flex items-center gap-2">
              <Bell className="w-4 h-4" /><h3 className="font-display font-semibold">Alertes</h3>
            </div>
            <div className="flex-1 overflow-y-auto max-h-[460px] divide-y divide-border">
              {alerts.length === 0 && <div className="p-6 text-sm text-muted-foreground">Aucune alerte.</div>}
              {alerts.map((a) => (
                <div key={a.id} className="p-4 flex items-start gap-3" data-testid={`alert-${a.id}`}>
                  <span className={`mt-1 w-2 h-2 rounded-full shrink-0 ${sevColor[a.severity] || "bg-slate-400"}`} />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium">{a.message}</div>
                    <div className="overline mt-1">{a.type}{a.resolved ? " · résolue" : ""}</div>
                  </div>
                  {!a.resolved && (
                    <Button size="sm" variant="ghost" className="text-xs" onClick={() => resolve(a.id)} data-testid={`resolve-${a.id}`}>OK</Button>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
          <div className="xl:col-span-2"><DispatchMessages /></div>
          <DispatchPTT />
        </div>

        {live.vehicles.length > 0 && (
          <div className="rounded-lg border border-border bg-card overflow-hidden" data-testid="live-table">
            <div className="px-5 py-3 border-b border-border flex items-center justify-between">
              <h3 className="font-display font-semibold">Autobus en service</h3>
              <span className="overline">Mise à jour automatique · 5 s</span>
            </div>
            <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-px bg-border">
              {live.vehicles.map((v) => {
                const d = v.delay_min || 0;
                const tone = d > 10 ? "bg-red-50 text-red-700 border-red-200"
                  : d > 5 ? "bg-amber-50 text-amber-700 border-amber-200"
                  : "bg-emerald-50 text-emerald-700 border-emerald-200";
                const label = d > 0 ? `+${d} min` : d < 0 ? `${d} min` : "à l'heure";
                return (
                  <div key={v.assignment_id} className="bg-card p-4" data-testid={`live-bus-${v.bus_fleet}`}>
                    <div className="flex items-center justify-between">
                      <span className="font-mono font-bold">Autobus {v.bus_fleet}</span>
                      <div className="flex items-center gap-1.5">
                        <Badge variant="outline">{v.period}</Badge>
                        <Badge>{v.route_number}</Badge>
                      </div>
                    </div>
                    <div className="text-sm mt-2 flex items-center justify-between">
                      <span>{v.driver_name}</span>
                      <Badge variant="secondary" data-testid={`live-variant-${v.bus_fleet}`}>
                        {v.variant === "original" ? "Original" : "Optimisé"}
                      </Badge>
                    </div>
                    <div className="h-1.5 rounded-full bg-secondary mt-3 overflow-hidden">
                      <div className="h-full bg-primary" style={{ width: `${v.progress_pct || 0}%` }} />
                    </div>
                    <div className="text-xs text-muted-foreground mt-1.5" data-testid={`live-progress-${v.bus_fleet}`}>
                      <span className="font-semibold text-foreground">Arrêt {v.current_index} sur {v.total_stops}</span>
                      {v.at_stop ? " · à l'arrêt" : " · en route"} · {v.speed_kmh != null ? `${v.speed_kmh} km/h` : "vitesse —"}
                    </div>
                    <div className="text-xs mt-1.5">
                      <span className="text-muted-foreground">En cours : </span>{v.current_stop?.address || "—"}
                    </div>
                    <div className="text-xs mt-0.5 truncate">
                      <span className="text-muted-foreground">Prochain : </span>{v.next_stop?.address || "—"}
                      {v.next_stop?.pickup_time ? ` · ${v.next_stop.pickup_time}` : ""}
                    </div>
                    <div className="text-[11px] mt-1.5 flex items-center gap-1.5" data-testid={`live-gps-${v.bus_fleet}`}>
                      <span className={`w-2 h-2 rounded-full ${v.gps_stale ? "bg-red-500 animate-pulse" : "bg-emerald-500"}`} />
                      <span className={v.gps_stale ? "text-red-600 font-semibold" : "text-muted-foreground"}>
                        {v.gps_age_sec == null ? "Aucun signal GPS"
                          : v.gps_stale ? `Signal perdu — il y a ${fmtAge(v.gps_age_sec)}`
                          : `GPS il y a ${fmtAge(v.gps_age_sec)}`}
                      </span>
                    </div>
                    <div className={`mt-3 rounded-md border px-2.5 py-1.5 text-xs font-semibold flex items-center justify-between ${tone}`}
                      data-testid={`live-delay-${v.bus_fleet}`}>
                      <span>{v.returns_to_garage ? "Retour garage" : "Arrivée école"} {v.eta_school?.eta || "—"}</span>
                      <span>{label}{v.eta_school?.limit ? ` · max ${v.eta_school.limit}` : ""}</span>
                    </div>
                    {v.eta_school && (
                      <div className="text-[11px] text-muted-foreground mt-1">
                        Reste {v.eta_school.remaining_km} km · {v.eta_school.remaining_min} min · {v.remaining_stops} arrêt(s)
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
